import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GoogleHotelCardData } from './hotelTypes';
import { Modal } from './Modal';

interface HotelCardProps {
  data: GoogleHotelCardData | GoogleHotelCardData[];
}

export const HotelCard: React.FC<HotelCardProps> = ({ data }) => {
  const { t } = useTranslation();
  const hotels = Array.isArray(data) ? data : [data];

  return (
    <div className="hotel-cards-wrapper">
      <div className="cards-section-header">
        <h2 className="cards-section-title">{t('hotelCard.sectionTitle')}</h2>
      </div>
      <div className="hotel-cards-container">
        {hotels.map((hotel) => <HotelCardItem key={hotel.id} hotel={hotel} />)}
      </div>
    </div>
  );
};

const HotelCardItem: React.FC<{ hotel: GoogleHotelCardData }> = ({ hotel }) => {
  const { t, i18n } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageUnavailable, setImageUnavailable] = useState(!hotel.imageUrl);
  const nearbyPlaces = hotel.nearbyPlaces.filter((place) => place.name).slice(0, 6);
  const formattedRating = hotel.rating?.toLocaleString(i18n.language, { maximumFractionDigits: 1 });

  const openModal = () => setIsModalOpen(true);

  return (
    <div className="hotel-card-wrapper">
      <div
        className="hotel-card"
        onClick={openModal}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') openModal();
        }}
        role="button"
        tabIndex={0}
      >
        <HotelImage
          className="hotel-card-image"
          imageUrl={hotel.imageUrl}
          hotelName={hotel.name}
          unavailable={imageUnavailable}
          onUnavailable={() => setImageUnavailable(true)}
        />

        <div className="hotel-card-content">
          <div className="hotel-card-header">
            <div className="hotel-info">
              <h3 className="hotel-name">{hotel.name}</h3>
              {(hotel.address || hotel.type) && (
                <span className="hotel-location">{hotel.address || hotel.type}</span>
              )}
            </div>
          </div>

          {(hotel.totalRate?.lowest || hotel.nightlyRate?.lowest) && (
            <div className="hotel-price-summary">
              {hotel.totalRate?.lowest && (
                <div className="hotel-price-primary">
                  <span className="hotel-price-label">{t('hotelCard.totalStay')}</span>
                  <span className="hotel-price-amount">{hotel.totalRate.lowest}</span>
                </div>
              )}
              {hotel.nightlyRate?.lowest && (
                <div className="hotel-price-secondary">
                  <span>{t('hotelCard.perNight')}</span>
                  <strong>{hotel.nightlyRate.lowest}</strong>
                </div>
              )}
            </div>
          )}

          {(hotel.hotelClass !== undefined || hotel.rating !== undefined) && (
            <div className="hotel-quality-summary">
              {hotel.hotelClass !== undefined && (
                <div className="hotel-quality-item hotel-category">
                  <span className="hotel-quality-label">{t('hotelCard.hotelCategory')}</span>
                  <strong className="hotel-quality-value">
                    <span className="hotel-category-stars" aria-hidden="true">
                      {'★'.repeat(Math.max(0, Math.min(5, Math.round(hotel.hotelClass))))}
                    </span>
                    <span>{t('hotelCard.starCount', { count: hotel.hotelClass })}</span>
                  </strong>
                </div>
              )}
              {hotel.rating !== undefined && (
                <div className="hotel-quality-item hotel-review-score">
                  <span className="hotel-quality-label">{t('hotelCard.guestRating')}</span>
                  <strong className="hotel-quality-value">
                    <span className="hotel-rating-value">{formattedRating}/5</span>
                    {hotel.reviews !== undefined && (
                      <span className="hotel-review-count">{t('hotelCard.reviewCount', { count: hotel.reviews })}</span>
                    )}
                  </strong>
                </div>
              )}
            </div>
          )}

          <div className="hotel-details">
            {hotel.freeCancellation === true && (
              <span className="detail-badge refundable">{t('hotelCard.freeCancellation')}</span>
            )}
          </div>

          {hotel.amenities.length > 0 && (
            <div className="hotel-amenity-preview">
              {hotel.amenities.slice(0, 3).map((amenity) => (
                <span className="amenity-chip" key={amenity}>{amenity}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={hotel.name}>
        <div className="hotel-modal-content">
          <HotelImage
            className="hotel-modal-hero"
            imageUrl={hotel.imageUrl}
            hotelName={hotel.name}
            unavailable={imageUnavailable}
            onUnavailable={() => setImageUnavailable(true)}
          />

          {hotel.description && (
            <section className="details-section">
              <h3>{t('hotelCard.description')}</h3>
              <p>{hotel.description}</p>
            </section>
          )}

          <section className="details-section">
            <h3>{t('hotelCard.stayDetails')}</h3>
            <div className="details-grid">
              <Detail label={t('hotelCard.checkIn')} value={`${hotel.checkInDate}${hotel.checkInTime ? ` · ${hotel.checkInTime}` : ''}`} />
              <Detail label={t('hotelCard.checkOut')} value={`${hotel.checkOutDate}${hotel.checkOutTime ? ` · ${hotel.checkOutTime}` : ''}`} />
              {hotel.address && <Detail label={t('hotelCard.address')} value={hotel.address} />}
              {hotel.hotelClass !== undefined && (
                <Detail label={t('hotelCard.hotelCategory')} value={t('hotelCard.starCount', { count: hotel.hotelClass })} />
              )}
              {formattedRating !== undefined && (
                <Detail label={t('hotelCard.guestRating')} value={`${formattedRating}/5`} />
              )}
              {hotel.reviews !== undefined && (
                <Detail label={t('hotelCard.reviews')} value={t('hotelCard.reviewCount', { count: hotel.reviews })} />
              )}
            </div>
          </section>

          {(hotel.nightlyRate || hotel.totalRate) && (
            <section className="details-section">
              <h3>{t('hotelCard.priceBreakdown')}</h3>
              <div className="details-grid">
                {hotel.nightlyRate?.lowest && <Detail label={t('hotelCard.perNight')} value={hotel.nightlyRate.lowest} />}
                {hotel.totalRate?.lowest && <Detail label={t('hotelCard.total')} value={hotel.totalRate.lowest} highlight />}
                {hotel.totalRate?.beforeTaxesAndFees && (
                  <Detail label={t('hotelCard.beforeTaxesFees')} value={hotel.totalRate.beforeTaxesAndFees} />
                )}
              </div>
            </section>
          )}

          {hotel.amenities.length > 0 && (
            <section className="details-section">
              <h3>{t('hotelCard.amenities')}</h3>
              <div className="amenities-list">
                {hotel.amenities.map((amenity) => (
                  <span className="amenity-chip" key={amenity}>{amenity}</span>
                ))}
              </div>
            </section>
          )}

          {nearbyPlaces.length > 0 && (
            <section className="details-section">
              <h3>{t('hotelCard.nearby')}</h3>
              <div className="nearby-places-grid">
                {nearbyPlaces.map((place) => (
                  <div className="nearby-place" key={place.name}>
                    <strong>{place.name}</strong>
                    {place.transportations?.length ? (
                      <div className="nearby-transportations">
                        {place.transportations.slice(0, 2).map((item, index) => (
                          <span key={`${item.type}-${item.duration}-${index}`}>
                            {formatTransportation(item.type, item.duration, t)}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </section>
          )}

          {hotel.link && (
            <a className="card-external-link" href={hotel.link} target="_blank" rel="noreferrer">{t('hotelCard.website')}</a>
          )}
        </div>
      </Modal>
    </div>
  );
};

interface HotelImageProps {
  className: string;
  imageUrl?: string;
  hotelName: string;
  unavailable: boolean;
  onUnavailable: () => void;
}

const HotelImage: React.FC<HotelImageProps> = ({
  className,
  imageUrl,
  hotelName,
  unavailable,
  onUnavailable
}) => {
  const { t } = useTranslation();

  return (
    <div className={className}>
      {!unavailable && imageUrl ? (
        <img src={imageUrl} alt={hotelName} onError={onUnavailable} />
      ) : (
        <div className="hotel-image-placeholder" role="img" aria-label={t('hotelCard.imageUnavailable')}>
          <svg viewBox="0 0 48 48" aria-hidden="true">
            <path d="M8 40V18l16-10 16 10v22H8Z" />
            <path d="M18 40V27h12v13M15 20h4M29 20h4" />
          </svg>
          <span>{t('hotelCard.imageUnavailable')}</span>
        </div>
      )}
    </div>
  );
};

const Detail: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className={`detail-row${highlight ? ' highlight' : ''}`}>
    <span className="detail-label">{label}</span>
    <span className="detail-value">{value}</span>
  </div>
);

function formatTransportation(
  type: string | undefined,
  duration: string | undefined,
  t: (key: string) => string
): string {
  const normalizedType = type?.trim().toLowerCase();
  const translationKey = normalizedType === 'walking'
    ? 'hotelCard.walking'
    : normalizedType === 'public transport'
      ? 'hotelCard.publicTransport'
      : normalizedType === 'driving'
        ? 'hotelCard.driving'
        : normalizedType === 'taxi'
          ? 'hotelCard.taxi'
          : undefined;
  const label = translationKey ? t(translationKey) : type;
  return [label, duration].filter(Boolean).join(' · ');
}
